import parse from 'html-react-parser';
import DOMPurify from 'dompurify';

import type { DeviceType, ViewMode } from '@/store/types';
import type { IAgentContract } from '@/api/types';
import type { ISchedule } from '@/types/schedule';
import type { IdHueMap } from '@/types/styles';

import { SCREEN_SIZE_TABLET } from '@/config/variables';
import { BaseHue } from '@/store/enums';

const getAllValues = (obj: Record<string, string | Record<string, string>>): string[] => {
  const result = [];
  for (const prop in obj) {
    const value = obj[prop];
    if (typeof value === 'object') {
      result.push(getAllValues(value));
    } else {
      result.push(value);
    }
  }
  return result.flat();
};

const fixHyphens = (text: string): string => text.replace(/-/g, '\u2011');

const isMobileHelper = (width: number): boolean => width < SCREEN_SIZE_TABLET;

const getDeviceType = (): DeviceType => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  } else if (
    /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)
  ) {
    return 'mobile';
  }
  return 'desktop';
};

const isSmallIOSMobile = (): boolean => {
  return ['iPhone', 'iPod'].includes(navigator.platform) && getViewMode() === 'portrait';
};

const isSafari = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.indexOf('safari') > 0 && ua.indexOf('chrome') < 0;
};

const getViewMode = (): ViewMode => {
  // due to the safari support
  if (isSafari()) return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
  return screen.availHeight > screen.availWidth ? 'portrait' : 'landscape';
};

const classList = (...classes: (string | boolean | undefined)[]): string =>
  classes.filter(item => Boolean(item)).join(' ');

const formatPhoneNumber = (number: string, phoneNumberDigits?: number): string => {
  if (phoneNumberDigits && phoneNumberDigits === 9) {
    const maskRegex = number.replace(/\D/g, '').match(/(\d{2})(\d{3})(\d{4})/);
    return maskRegex ? `${maskRegex[1]} ${maskRegex[2]}-${maskRegex[3]}` : '';
  } else {
    const maskRegex = number.replace(/\D/g, '').match(/(\d{3})(\d{3})(\d{4})/);
    return maskRegex ? `(${maskRegex[1]}) ${maskRegex[2]}-${maskRegex[3]}` : '';
  }
};

const isOfType = <T>(varToBeChecked: unknown, propertyToCheckFor: keyof T): varToBeChecked is T =>
  (varToBeChecked as T)[propertyToCheckFor] !== undefined;

const findNestedObj = (
  entireObj: any[],
  keyToFind: string,
  valToFind: unknown,
  isMatch = false,
): Record<string | number | symbol, any> | undefined => {
  let foundObj;
  JSON.stringify(entireObj, (_, nestedValue) => {
    if (nestedValue) {
      if (
        !isMatch
          ? nestedValue[keyToFind] === valToFind
          : String(nestedValue[keyToFind]).toLowerCase().match(String(valToFind).toLowerCase())
      ) {
        foundObj = nestedValue;
      }
    }
    return nestedValue;
  });
  return foundObj;
};

const findParentObj = (
  entireObj: any[],
  keyToFind: string,
  childObject?: Record<string | number | symbol, any>,
): Record<string | number | symbol, any> | undefined => {
  if (!childObject) return;
  let foundObj;
  JSON.stringify(entireObj, (_, nestedValue) => {
    if (nestedValue && nestedValue[keyToFind]?.some((item: unknown) => item === childObject)) {
      foundObj = nestedValue;
    }
    return nestedValue;
  });
  return foundObj;
};

const html = (html: string): ReturnType<typeof parse> => parse(DOMPurify.sanitize(html));

const currencyFormat = (num: string | number, currencySymbol: string): string => {
  const newNum: number = +clearSpecialCharacters(num);
  return currencySymbol + newNum.toFixed(2).replace(/([^0-9.]+)/g, `${currencySymbol}1,`);
};

const clearSpecialCharacters = (num: string | number): string => {
  return (+num.toString().replace(/([^0-9.]+)/g, '')).toString();
};

const removeDecimal = (value: string | number): string => {
  return value.toString().split('.')[0];
};

const redirectToExternal = (url: string, target: string): void => {
  window.open(url, target);
};

const makeArraySet = <T>(array: T[], key: keyof T): T[] => {
  const objectSet: T[] = [];
  for (const item of array) {
    if (!objectSet.find(obj => obj[key] === item[key])) {
      objectSet.push(item);
    }
  }
  return objectSet;
};

const _prepareColorsMap = <T>(array: T[], idKey: keyof T): IdHueMap => {
  const definedColors = Object.values(BaseHue);
  return array.reduce<IdHueMap>((acc, obj, index) => {
    acc[obj[idKey] as unknown as number] =
      definedColors[index >= definedColors.length ? definedColors.length - 1 : index];
    return acc;
  }, {});
};

const mapScheduleColors = (schedules: ISchedule[]): IdHueMap => _prepareColorsMap(schedules, 'scheduleId');
const mapContactColors = (contracts: IAgentContract[]): IdHueMap => _prepareColorsMap(contracts, 'contractId');

const isEmpty = (value: any) => {
  if (value === undefined || value === null || (typeof value === 'number' && isNaN(value))) {
    return true;
  }

  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === 'object') {
    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        return false;
      }
    }
    return true;
  }

  return false;
};

const isNullOrUndefined = (value: any) => {
  return value === undefined || value === null;
};

const cloneDeep = (value: any): any => {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    const newArray = [];
    for (const item of value) {
      newArray.push(cloneDeep(item));
    }
    return newArray;
  }

  if (typeof value === 'object') {
    const newObject: Record<string, any> = {};
    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        newObject[key] = cloneDeep(value[key]);
      }
    }
    return newObject;
  }
};

const noop = () => {
  // do nothing
};

const get = <T>(object: Record<string, any>, path: string | string[], defaultValue: T): T => {
  const keys = Array.isArray(path) ? path : path.split('.');
  let result: any = object;

  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = result[key];
    } else {
      return defaultValue;
    }
  }

  return result !== undefined ? result : defaultValue;
};

const maxBy = <T>(collection: T[], iteratee: string | ((item: T) => number)): T | undefined => {
  let result = undefined;
  if (typeof iteratee === 'function') {
    result = collection.reduce((maxItem: any, currentItem: T) => {
      const max = iteratee(maxItem);
      const current = iteratee(currentItem);
      return current > max ? currentItem : maxItem;
    }, collection[0]);
  } else if (typeof iteratee === 'string') {
    result = collection.reduce((maxItem: any, currentItem: any) => {
      const max = maxItem[iteratee];
      const current = currentItem[iteratee];
      return current > max ? currentItem : maxItem;
    }, collection[0]);
  }
  return result;
};

const sortBy = <T>(collection: T[], ...keys: string[]): T[] => {
  return collection.slice().sort((a, b) => {
    for (const key of keys) {
      const keyParts = key.split('.');
      let aValue: any = a;
      let bValue: any = b;

      for (const part of keyParts) {
        if (aValue && aValue.hasOwnProperty(part)) {
          aValue = aValue[part];
        } else {
          aValue = undefined;
          break;
        }
      }

      for (const part of keyParts) {
        if (bValue && bValue.hasOwnProperty(part)) {
          bValue = bValue[part];
        } else {
          bValue = undefined;
          break;
        }
      }

      if (aValue < bValue) {
        return -1;
      }
      if (aValue > bValue) {
        return 1;
      }
    }
    return 0;
  });
};

interface HasToString {
  toString(): string;
}

const groupBy = <T>(
  collection: T[] | null | undefined,
  iteratee: ((item: T) => any) | keyof T,
): Record<string, T[]> => {
  const groups: Record<string, T[]> = {};

  if (!collection || !iteratee) {
    return groups;
  }

  for (const item of collection) {
    const key = typeof iteratee === 'function' ? iteratee(item).toString() : (item[iteratee] as HasToString).toString();

    if (!groups[key]) {
      groups[key] = [];
    }

    groups[key].push(item);
  }

  return groups;
};

type DebounceSettings = {
  leading?: boolean;
  maxWait?: number;
};

type DebouncedFunc<T extends (...args: any[]) => any> = {
  (...args: Parameters<T>): ReturnType<T> | undefined;
  cancel(): void;
};

const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait = 0,
  options: DebounceSettings = {},
): DebouncedFunc<T> => {
  let timeout: NodeJS.Timeout | null = null;

  const debounced = (...args: Parameters<T>): ReturnType<T> | undefined => {
    const context = this as any;

    const later = () => {
      timeout = null;
      if (!options.leading) {
        return func.apply(context, args);
      }
    };

    const callNow = options.leading && !timeout;

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, wait);

    if (callNow) {
      return func.apply(context, args);
    }

    return undefined;
  };

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
};

const getHtmlStringHeight = (htmlString: string, width: number): number => {
  const tempContainer = document.createElement('div');
  tempContainer.style.position = 'absolute';
  tempContainer.style.visibility = 'hidden';
  tempContainer.style.width = width + 'px';
  tempContainer.style.whiteSpace = 'normal';
  tempContainer.innerHTML = htmlString;
  document.body.appendChild(tempContainer);
  const height = tempContainer.getBoundingClientRect().height;
  document.body.removeChild(tempContainer);
  return height;
};

const getHtmlSubStringByHeight = (htmlString: string, width: number, height: number) => {
  let htmlSubstring = '';
  for (let i = 0; i < htmlString.length; i++) {
    htmlSubstring += htmlString[i];
    if (getHtmlStringHeight(htmlSubstring, width) >= height) {
      return htmlSubstring.slice(0, -1);
    }
  }
  return htmlString;
};

export {
  classList,
  clearSpecialCharacters,
  currencyFormat,
  findNestedObj,
  findParentObj,
  fixHyphens,
  formatPhoneNumber,
  getAllValues,
  getDeviceType,
  getViewMode,
  html,
  isSafari,
  isSmallIOSMobile,
  isMobileHelper,
  isOfType,
  isEmpty,
  isNullOrUndefined,
  noop,
  get,
  cloneDeep,
  maxBy,
  sortBy,
  groupBy,
  debounce,
  makeArraySet,
  redirectToExternal,
  removeDecimal,
  mapScheduleColors,
  mapContactColors,
  getHtmlStringHeight,
  getHtmlSubStringByHeight,
};
